
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, MapPin, MessageCircle, User, ArrowLeft, Filter, GripVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { matchService } from "@/services/matchService";
import { toast } from "@/hooks/use-toast";
import { profileRouterService } from "@/services/profileRouterService";
import { useDiscoveryFilters } from "@/hooks/useDiscoveryFilters";
import DiscoveryFilters from "@/components/discovery/DiscoveryFilters";
import { favoritesService } from "@/services/favoritesService";

interface FavoriteUser {
  user_id: string;
  display_name: string;
  age: number;
  photo_urls: string[];
  bio: string;
  location: string;
  public_interests: string[];
  looking_for?: string[];
}

const Favorites = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const [favorites, setFavorites] = useState<FavoriteUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const { showFilters, setShowFilters, filters, setFilters, hasActiveFilters } = useDiscoveryFilters();

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user, filters]);

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data = await favoritesService.getFavoritesFiltered(user.id, filters);
      
      const favoriteUsers: FavoriteUser[] = data.map((fav: any) => {
        const profile = fav.user_profiles;
        return {
          user_id: profile.user_id,
          display_name: profile.display_name || 'Unknown User',
          age: profile.age || 0,
          photo_urls: profile.photo_urls || [],
          bio: profile.bio || '',
          location: profile.location || '',
          public_interests: profile.public_interests || [],
          looking_for: profile.looking_for || []
        };
      });

      setFavorites(favoriteUsers);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        title: "Error",
        description: "Failed to load favorites",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = (favoriteUserId: string, displayName: string) => {
    try {
      const conversationId = matchService.createConversationId(user!.id, favoriteUserId);
      navigate(`/messages/${conversationId}`);
      
      toast({
        title: t('success'),
        description: `${t('start_conversation')} ${displayName}`,
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('loading_error'),
        variant: "destructive"
      });
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newFavorites = [...favorites];
    const draggedItem = newFavorites[draggedIndex];
    
    // Remove dragged item
    newFavorites.splice(draggedIndex, 1);
    
    // Insert at new position
    newFavorites.splice(targetIndex, 0, draggedItem);
    
    setFavorites(newFavorites);
    setDraggedIndex(null);

    toast({
      title: "Favoriten neu angeordnet",
      description: "Die Reihenfolge wurde aktualisiert",
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-background flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
        <AdPanel />
        <RightSideMenu />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg text-muted-foreground">{t('loading')}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background text-foreground flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
      <AdPanel />
      <RightSideMenu />
      
      <div className="flex-1 p-4 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/discovery')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-2xl font-bold flex-1">{t('favorites') || 'Favorites'}</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowFilters(true)}
            className={`text-muted-foreground hover:text-foreground relative ${hasActiveFilters ? 'text-primary' : ''}`}
          >
            <Filter className="w-6 h-6" />
            {hasActiveFilters && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
            )}
          </Button>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold text-muted-foreground mb-2">No favorites yet</h2>
            <p className="text-muted-foreground/70 mb-6">Start adding profiles to your favorites to see them here</p>
            <Button onClick={() => navigate('/discovery')} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Explore Profiles
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {favorites.map((favorite, index) => (
              <Card 
                key={favorite.user_id} 
                className={`bg-card border-border shadow-sm transition-all duration-200 ${
                  draggedIndex === index ? 'opacity-50 scale-95' : 'hover:shadow-md'
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Drag Handle */}
                    <div className="flex-shrink-0 flex items-center">
                      <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab active:cursor-grabbing" />
                    </div>
                    {/* Profile Photo */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-lg overflow-hidden">
                        {favorite.photo_urls && favorite.photo_urls.length > 0 ? (
                          <img
                            src={favorite.photo_urls[0]}
                            alt={favorite.display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <User className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Profile Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-card-foreground truncate">
                          {favorite.display_name}
                          {favorite.age && <span className="text-muted-foreground ml-2">{favorite.age}</span>}
                        </h3>
                        <Heart className="w-5 h-5 text-primary fill-current" />
                      </div>

                      {favorite.location && (
                        <div className="flex items-center text-muted-foreground mb-2">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span className="text-sm truncate">{favorite.location}</span>
                        </div>
                      )}

                      {favorite.bio && (
                        <p className="text-card-foreground/80 text-sm mb-3 line-clamp-2">{favorite.bio}</p>
                      )}

                      {/* Interests */}
                      {favorite.public_interests && favorite.public_interests.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {favorite.public_interests.slice(0, 3).map((interest, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              #{interest}
                            </Badge>
                          ))}
                          {favorite.public_interests.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{favorite.public_interests.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleMessage(favorite.user_id, favorite.display_name)}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Message
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(profileRouterService.getProfileRoute(favorite.user_id, favorite.display_name))}
                          className="border-border text-muted-foreground hover:text-foreground hover:border-border"
                        >
                          View Profile
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <DiscoveryFilters
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onReset={() => setFilters({
          ageMin: 18,
          ageMax: 99,
          maxDistance: 0,
          onlineOnly: false,
          withPhotoOnly: false,
          lookingFor: []
        })}
      />
    </div>
  );
};

export default Favorites;
